/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { LearningExperienceTabType } from '@app/app/page-modules/folio/v3-components/pages/learner-record/learner-record.component';
import {
  AchievementType,
  CreateExternalLearningExperienceRequest,
  ExternalLearningExperience,
  LearningExperience,
  UpdateLearningExperienceRequest,
} from '@app/app/page-modules/learning-experiences/learning-experience.model';
import { Page } from '@app/app/shared/models/page';
import {
  FileUploadPresignedUrlRequest,
  FileUploadPresignedUrlResponseDetailed,
} from '@app/app/shared/services/file-upload/file-upload.model';
import { LearningExperiencesService } from '@app/app/shared/services/learning-experiences/learning-experiences.service';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { environment } from '@app/environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface AchievementTypeCount {
  type: LearningExperienceTabType;
  count: number;
}

export interface FindLerExpInitialResponse {
  learningExperiences: Page<LearningExperience>;
  perTypeCount: Map<AchievementType, number>;
  isEmpty: boolean;
}

@Injectable()
export class ApiLearningExperiencesService implements LearningExperiencesService {
  constructor(private client: RestClientService) {}

  getLearningExperiences(
    achievementType: AchievementType | null,
    term: string | null,
    page: number,
    size: number,
  ): Observable<Page<LearningExperience>> {
    const reqParams: { [key: string]: string } = {
      page: page.toString(),
      pageSize: size.toString(),
    };

    if (achievementType) {
      reqParams.achievementType = achievementType;
    }
    if (term) {
      reqParams.term = term;
    }

    return this.client
      .get<Page<LearningExperience>>(Location.joinWithSlash(environment.apiRootUrl || '', `learning-experiences/search`), {
        ...reqParams,
      })
      .pipe(map((response) => response.body));
  }

  getLearningExperiencesInitialView(size: number): Observable<FindLerExpInitialResponse> {
    return this.client
      .get<FindLerExpInitialResponse>(Location.joinWithSlash(environment.apiRootUrl || '', `learning-experiences/search-with-count`), {
        pageSize: size.toString(),
      })
      .pipe(map((response) => response.body));
  }

  createExternalLearningExperience(request: CreateExternalLearningExperienceRequest): Observable<ExternalLearningExperience> {
    const apiPath = Location.joinWithSlash(environment.apiRootUrl || '', '/learning-experiences/external/create');

    return this.client.post<ExternalLearningExperience>(apiPath, request).pipe(map((response) => response.body));
  }

  removeLearningExperience(learningExperienceUid: string): Observable<void> {
    const apiPath = Location.joinWithSlash(environment.apiRootUrl || '', `/learning-experiences/${learningExperienceUid}`);

    return this.client.delete<void>(apiPath).pipe(map((response) => response.body));
  }

  updateLearningExperience(learningExperienceUid: string, request: UpdateLearningExperienceRequest): Observable<LearningExperience> {
    const apiPath = Location.joinWithSlash(environment.apiRootUrl || '', `/learning-experiences/${learningExperienceUid}`);

    return this.client.post<LearningExperience>(apiPath, request).pipe(map((response) => response.body));
  }

  generateAttachmentUploadUrl(attachments: File[]): Observable<FileUploadPresignedUrlResponseDetailed[]> {
    const apiPath = Location.joinWithSlash(environment.apiRootUrl || '', '/learning-experiences/attachments/upload-url');
    const request: FileUploadPresignedUrlRequest[] = attachments.map((attachmentFile) =>
      this.toFileUploadPresignedUrlRequest(attachmentFile),
    );

    return this.client.post<FileUploadPresignedUrlResponseDetailed[]>(apiPath, request).pipe(map((response) => response.body));
  }

  getLearningExperiencesCount(): Observable<number> {
    return this.client
      .get<number>(Location.joinWithSlash(environment.apiRootUrl || '', 'learning-experiences/count'))
      .pipe(map((response) => response.body));
  }

  private toFileUploadPresignedUrlRequest(file: File): FileUploadPresignedUrlRequest {
    return {
      fileName: file.name,
      contentLength: file.size.toString(),
    };
  }
}
